import React, { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { toast } from "sonner";
import { OtcRequestItem } from "../types";
import {
  // AddNewProviderForm,
  CompleteTransferStepper,
  // NewProviderData,
  PaymentInfoData,
  PaymentInformationStep,
  ProviderItem,
  ProviderSelectionStep,
  ReviewTransferDetailsStep,
  TransferSubmittedSuccessModal,
} from "../components/complete-transfer";
import {
  DomesticRecipient,
  ExternalRecipient,
  settleOtcRequest,
} from "../services";
import { getApiErrorMessage } from "../../../shared/lib";
import { TransactionItem } from "../../transactions/types";
import { fetchTransactionById, downloadTransactionReceipt } from "../../transactions/services";
import { mapTransactionToOtcRequest } from "../../transactions/components/details/mapTransactionToOtcRequest";
import { Button, Shimmer } from "../../../shared/ui";
import OtpModal from "../../account-settings/components/OtpModal";

export type CompleteTransferStep =
  | "provider"
  | "new_provider"
  | "payment_info"
  | "review";

export const CompleteTransferPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  // Retrieve passed OTC request data from router state
  const passedRequest = location.state?.request as OtcRequestItem | undefined;
  const passedTransaction = location.state?.transaction as TransactionItem | undefined;
  const passedOtcId = location.state?.otcId as string | undefined;

  // Always query live transaction by ID if needed so we have the transaction and its otc subdocument
  const { data: trxResult, isLoading } = useQuery({
    queryKey: ["transaction", id],
    queryFn: () => fetchTransactionById(id),
    enabled: Boolean(id) && (!passedRequest || !passedOtcId),
  });

  const currentRequest: OtcRequestItem | null = useMemo(() => {
    if (passedRequest && (passedRequest.otcId || passedOtcId)) {
      return {
        ...passedRequest,
        otcId: passedOtcId || passedRequest.otcId,
      };
    }
    if (trxResult?.data) {
      return mapTransactionToOtcRequest(trxResult.data, id);
    }
    if (passedRequest) return passedRequest;
    return null;
  }, [passedRequest, trxResult, id, passedOtcId]);

  // Extract the OTC ID found in the otc object (strictly never the transaction ID from the URL)
  const resolvedOtcId = useMemo(() => {
    const rawOtc =
      (trxResult?.data?.otc as Record<string, any> | undefined) ||
      (passedTransaction?.otc as Record<string, any> | undefined);

    const fromOtcObj = rawOtc?._id || rawOtc?.id || rawOtc?.otcId;
    if (fromOtcObj) return String(fromOtcObj);

    if (passedOtcId && passedOtcId !== id) return passedOtcId;
    if (currentRequest?.otcId && currentRequest.otcId !== id) return currentRequest.otcId;

    // If currentRequest.id is distinct from the transaction ID from the URL, it is the otc ID
    if (currentRequest?.id && currentRequest.id !== id) return currentRequest.id;

    return undefined;
  }, [trxResult, passedTransaction, passedOtcId, currentRequest, id]);

  const [currentStep, setCurrentStep] =
    useState<CompleteTransferStep>("provider");
  const [selectedProvider, setSelectedProvider] = useState<ProviderItem | null>(
    null
  );
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfoData>({
    note: "",
    invoiceFile: null,
    invoiceFileName: "",
    supportingDocFile: null,
    supportingDocFileName: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);

  // Active step index (1, 2, or 3) for the stepper
  const activeStepIdx =
    currentStep === "provider" || currentStep === "new_provider"
      ? 1
      : currentStep === "payment_info"
        ? 2
        : 3;

  const handleExit = () => {
    navigate(-1);
  };

  const handleSelectProvider = (prov: ProviderItem) => {
    setSelectedProvider(prov);
  };

  // const handleAddNewProviderConfirm = (data: NewProviderData) => {
  //   const fullName =
  //     data.accountName ||
  //     `${data.firstName} ${data.lastName}`.trim();
  //
  //   const isDomestic =
  //     data.recipientLocation === "NOT_US" ||
  //     data.recipientLocation === "Outside the US" ||
  //     data.recipientLocation === "Recipient outside the US";
  //
  //   const createdProvider: ProviderItem = {
  //     id: "",
  //     name: fullName,
  //     accountNumber: data.accountNumber,
  //     companyName: data.bank,
  //     avatarText: fullName.slice(0, 2).toUpperCase(),
  //     bankCode: data.bankCode,
  //     bankCountry: data.bankCountry,
  //     paymentMethod: data.paymentMethod,
  //     nameEnquiryReference: data.nameEnquiryReference,
  //     relationship: data.relationship,
  //     accountHolderType: data.accountHolderType,
  //     email: data.email,
  //     routingNumber: data.routingNumber,
  //     swiftCode: data.swiftCode,
  //     streetAddress: data.streetAddress,
  //     streetAddress2: data.streetAddress2,
  //     city: data.city,
  //     state: data.state,
  //     zipCode: data.zipCode,
  //     addressCountry: data.addressCountry,
  //     isDomestic,
  //   };
  //   setSelectedProvider(createdProvider);
  //   setCurrentStep("payment_info");
  // };

  const handlePaymentInfoNext = (data: PaymentInfoData) => {
    setPaymentInfo(data);
    setCurrentStep("review");
  };

  const handleInitiateSubmit = () => {
    if (!currentRequest) {
      toast.error("Transaction details not available");
      return;
    }

    if (!selectedProvider) {
      toast.error("Please select or add a recipient provider before proceeding");
      return;
    }

    // Trigger MFA OTP modal
    setIsOtpModalOpen(true);
  };

  const handleFinalSubmitWithMfa = async (mfaCode: string) => {
    if (!currentRequest) {
      toast.error("Transaction details not available");
      return;
    }

    if (!selectedProvider) {
      toast.error("Please select or add a recipient provider before proceeding");
      return;
    }

    // Primary: OTC ID from the OTC object. Fallback: transaction ID from the URL
    const otcIdentifier =
      resolvedOtcId ||
      currentRequest.otcId ||
      (currentRequest.id !== id ? currentRequest.id : undefined) ||
      id ||
      currentRequest.id ||
      currentRequest.transactionId;

    if (!otcIdentifier) {
      toast.error("Transaction identifier not available");
      return;
    }

    // Determine settlement type based on the active transaction currency.
    // If currency is not USD (e.g. NGN), use domestic settlement payload.
    // If currency is USD, use external settlement payload (US or international).
    const isDomesticPayout = currentRequest.currency !== "USD";

    setIsSubmitting(true);

    try {
      if (!isDomesticPayout) {
        // External US / International USD Settlement
        const externalAccountId =
          selectedProvider.id &&
            !selectedProvider.id.startsWith("prov-") &&
            !selectedProvider.id.startsWith("req-prov-")
            ? selectedProvider.id
            : undefined;

        if (externalAccountId) {
          // If externalAccountId exists, DO NOT include the recipient object
          await settleOtcRequest(otcIdentifier, {
            externalAccountId,
            narration: paymentInfo.note || "OTC USD payout",
            mfaCode,
          });
        } else {
          // If externalAccountId does NOT exist, validate and include recipient without externalAccountId
          if (
            !selectedProvider.accountNumber ||
            !selectedProvider.name ||
            !selectedProvider.companyName ||
            !selectedProvider.streetAddress ||
            !selectedProvider.city ||
            !selectedProvider.state ||
            !selectedProvider.zipCode
          ) {
            toast.error(
              "Incomplete recipient details. Account number, name, bank, and address are required."
            );
            setIsSubmitting(false);
            return;
          }

          const isOutsideUs =
            selectedProvider.isDomestic ||
            Boolean(selectedProvider.swiftCode) ||
            selectedProvider.paymentMethod === "IBAN / SWIFT";

          if (isOutsideUs && !selectedProvider.swiftCode) {
            toast.error("SWIFT or BIC is required for recipient outside the US");
            setIsSubmitting(false);
            return;
          }

          if (!isOutsideUs && !selectedProvider.routingNumber) {
            toast.error("Routing number (ABA) is required for US recipient");
            setIsSubmitting(false);
            return;
          }

          const externalRecipient: ExternalRecipient = {
            accountNumber: selectedProvider.accountNumber,
            accountName: selectedProvider.name,
            account_owner_name: selectedProvider.name,
            account_type: isOutsideUs ? "international" : "us",
            bank_name: selectedProvider.companyName,
            account: {
              account_number: selectedProvider.accountNumber,
              ...(selectedProvider.routingNumber
                ? { routing_number: selectedProvider.routingNumber }
                : {}),
              ...(selectedProvider.swiftCode
                ? { swift_code: selectedProvider.swiftCode }
                : {}),
              checking_or_savings: "checking",
            },
            address: {
              street_line_1: selectedProvider.streetAddress || "",
              ...(selectedProvider.streetAddress2
                ? { street_line_2: selectedProvider.streetAddress2 }
                : {}),
              city: selectedProvider.city || "",
              state: selectedProvider.state || "",
              postal_code: selectedProvider.zipCode || "",
              country: selectedProvider.addressCountry || "",
            },
            bank_address: {
              street_line_1: selectedProvider.streetAddress || "",
              city: selectedProvider.city || "",
              state: selectedProvider.state || "",
              postal_code: selectedProvider.zipCode || "",
              country: selectedProvider.bankCountry || selectedProvider.addressCountry || "",
            },
          };

          await settleOtcRequest(otcIdentifier, {
            recipient: externalRecipient,
            narration: paymentInfo.note || "OTC USD payout",
            mfaCode,
          });
        }
      } else {
        // Domestic / Non-USD Settlement (e.g. NGN)
        const externalAccountId =
          selectedProvider.id &&
            !selectedProvider.id.startsWith("prov-") &&
            !selectedProvider.id.startsWith("req-prov-")
            ? selectedProvider.id
            : undefined;

        if (externalAccountId) {
          // If externalAccountId exists, DO NOT include the recipient object
          await settleOtcRequest(otcIdentifier, {
            externalAccountId,
            narration: paymentInfo.note || "OTC payout",
            mfaCode,
          });
        } else {
          // If externalAccountId does NOT exist, validate and include recipient without externalAccountId
          if (
            !selectedProvider.accountNumber ||
            !selectedProvider.name ||
            !selectedProvider.companyName
          ) {
            toast.error(
              "Incomplete domestic recipient details. Account number, recipient name, and bank details are required."
            );
            setIsSubmitting(false);
            return;
          }

          const domesticRecipient: DomesticRecipient = {
            accountNumber: selectedProvider.accountNumber,
            accountName: selectedProvider.name,
            bankCode: selectedProvider.bankCode || "",
            bank_name: selectedProvider.companyName,
            nameEnquiryReference: selectedProvider.nameEnquiryReference || "",
          };

          await settleOtcRequest(otcIdentifier, {
            recipient: domesticRecipient,
            narration: paymentInfo.note || "OTC payout",
            mfaCode,
          });
        }
      }

      toast.success("OTC settlement payout initiated successfully");
      setIsOtpModalOpen(false);
      setIsSuccessModalOpen(true);
    } catch (error) {
      console.error("OTC settlement error:", error);
      toast.error(
        getApiErrorMessage(
          error,
          "Settlement payout request failed. Please check recipient bank details and retry."
        )
      );
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadReceipt = () => {
    const targetTrxId = id || currentRequest?.transactionId || currentRequest?.id;
    if (targetTrxId) {
      downloadTransactionReceipt(targetTrxId);
    }
  };

  const handleDone = () => {
    setIsSuccessModalOpen(false);
    handleExit();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <header className="sticky top-0 z-30 flex h-[72px] w-full items-center border-b border-neutral-200 bg-white px-6 md:px-12">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2.5 text-neutral-900"
          >
            <X size={20} className="text-neutral-500" />
            <span className="text-[15px] font-semibold">Complete OTC transfer</span>
          </button>
        </header>
        <main className="mx-auto max-w-[560px] py-12 px-4 space-y-4">
          <Shimmer className="h-10 w-full rounded-xl" />
          <Shimmer className="h-14 w-full rounded-xl" />
          <Shimmer className="h-16 w-full rounded-xl" />
          <Shimmer className="h-32 w-full rounded-xl" />
        </main>
      </div>
    );
  }

  if (!currentRequest) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <header className="sticky top-0 z-30 flex h-[72px] w-full items-center border-b border-neutral-200 bg-white px-6 md:px-12">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2.5 text-neutral-900"
          >
            <X size={20} className="text-neutral-500" />
            <span className="text-[15px] font-semibold">Complete OTC transfer</span>
          </button>
        </header>
        <main className="mx-auto max-w-[560px] py-16 px-4 text-center">
          <h2 className="text-[18px] font-semibold text-neutral-900">
            Transaction not found
          </h2>
          <p className="mt-2 text-[14px] text-neutral-500">
            Could not find an OTC request associated with &ldquo;{id}&rdquo;.
          </p>
          <div className="mt-6 flex justify-center">
            <Button type="button" onClick={() => navigate(-1)}>
              Go back
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Top Bar Header: Only Exit button on the left */}
      <header className="sticky top-0 z-30 flex h-[72px] w-full items-center  !justify-start border-b border-neutral-200 bg-white px-6 md:px-12">
        <button
          type="button"
          onClick={handleExit}
          className="group flex items-center gap-2.5 text-neutral-900 transition-colors hover:text-neutral-600 focus:outline-none"
        >
          <X size={20} className="text-neutral-500 transition-colors group-hover:text-neutral-900" />
          <span className="text-[15px] font-semibold text-neutral-900">
            Complete OTC transfer
          </span>
        </button>
      </header>

      {/* Main Content Area */}
      <main className="w-full py-8 md:py-12 px-4">
        <div className="mx-auto max-w-[560px]">
          {/* Stepper with the 3 circular icons directly above form fields */}
          <CompleteTransferStepper activeStep={activeStepIdx} />

          <div className="">
          {/* Form Step Content */}
          {currentStep === "provider" && (
            <ProviderSelectionStep
              request={currentRequest}
              selectedProviderId={selectedProvider?.id || null}
              onSelectProvider={handleSelectProvider}
              onAddNewProviderClick={() => setCurrentStep("new_provider")}
              onBack={handleExit}
              onNext={() => setCurrentStep("payment_info")}
            />
          )}

            {/* Add new provider flow commented out */}
            {/* {currentStep === "new_provider" && (
            <AddNewProviderForm
              request={currentRequest}
              onBack={() => setCurrentStep("provider")}
              onConfirm={handleAddNewProviderConfirm}
            />
          )} */}

          {currentStep === "payment_info" && (
            <PaymentInformationStep
              initialData={paymentInfo}
              onBack={() => setCurrentStep("provider")}
              onNext={handlePaymentInfoNext}
            />
          )}

          {currentStep === "review" && (
            <ReviewTransferDetailsStep
              request={currentRequest}
              provider={selectedProvider}
              paymentInfo={paymentInfo}
              onBack={() => setCurrentStep("payment_info")}
                onSubmit={handleInitiateSubmit}
              isSubmitting={isSubmitting}
            />
          )}
          </div>
        </div>
      </main>

      {/* MFA Verification Dialog */}
      <OtpModal
        isOpen={isOtpModalOpen ? "confirm-transfer" : ""}
        onOpenChange={(val) => setIsOtpModalOpen(Boolean(val))}
        handleAdminChangePassword={handleFinalSubmitWithMfa}
        isLoading={isSubmitting}
        onSuccess={() => { }}
      />

      {/* Success Dialog */}
      <TransferSubmittedSuccessModal
        open={isSuccessModalOpen}
        onClose={handleDone}
        onDownloadReceipt={handleDownloadReceipt}
      />
    </div>
  );
};

export default CompleteTransferPage;
